import { inject, Injectable, Injector } from '@angular/core';
import { defer, from, Observable } from 'rxjs';
import { finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';

/**
 * Renueva la sesión sin sacar al usuario de la pantalla.
 *
 * Existe porque antes un 401 cerraba la sesión y mandaba al login: el usuario
 * que tardaba en capturar perdía todo al dar Guardar.
 *
 * Si varias peticiones reciben 401 a la vez, todas esperan al mismo diálogo.
 */
@Injectable({ providedIn: 'root' })
export class ReautenticacionService {
  private readonly injector = inject(Injector);
  private readonly auth = inject(AuthService);
  private pendiente: Observable<boolean> | null = null;

  /** Emite `true` si el usuario volvió a entrar; `false` si eligió salir. */
  renovar(): Observable<boolean> {
    this.pendiente ??= defer(() => this.abrirDialogo()).pipe(
      finalize(() => (this.pendiente = null)),
      shareReplay(1),
    );
    return this.pendiente;
  }

  private abrirDialogo(): Observable<boolean> {
    const usuario = this.auth.currentUser();
    if (!usuario) {
      this.auth.logout();
      return from([false]);
    }

    // Carga diferida: el diálogo solo se descarga la primera vez que se usa.
    return from(Promise.all([import('@angular/material/dialog'), import('./sesion-expirada')])).pipe(
      switchMap(([{ MatDialog }, { SesionExpiradaDialog }]) =>
        this.injector
          .get(MatDialog)
          .open(SesionExpiradaDialog, {
            data: { usuario: usuario.usuario, nombre: usuario.nombre },
            disableClose: true,
            width: '420px',
          })
          .afterClosed(),
      ),
      map((ok) => {
        if (ok !== true) this.auth.logout();
        return ok === true;
      }),
    );
  }
}
